import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, Users, BarChart3, HelpCircle, Lightbulb, ThumbsUp, ThumbsDown, Bookmark, BookmarkCheck, ChevronDown, ChevronUp, MessageSquare, Headphones, Loader2, FileText } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { libraryAPI, articlesAPI } from '../api';
import { toast } from 'sonner';
import type { Article } from '../types';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import DiscussModal from './DiscussModal';
import { CrossAppLinks } from './CrossAppLinks';

interface ArticleCardProps {
  article: Article & {
    population?: string;
    study_size?: string;
    key_questions?: string;
  };
  showFeedback?: boolean;
  context?: 'general' | 'library' | 'digest' | 'explore';
  inLibrary?: boolean;
  onLibraryChange?: (pmid: string, saved: boolean) => void;
  // Phase 6: Discuss flow — optional specialty context for article-linked threads
  specialtyId?: string;
}

const ArticleCard: React.FC<ArticleCardProps> = ({ 
  article, 
  showFeedback = false, 
  context = 'general', 
  inLibrary = false, 
  onLibraryChange,
  specialtyId,
}) => {
  const navigate = useNavigate();
  const communityV2 = useFeatureFlag('enable_community_v2');
  const digestAudioLinks = useFeatureFlag('enable_digest_article_audio_links');
  const [feedback, setFeedback] = useState<string | null>(null);
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [isInLibrary, setIsInLibrary] = useState(inLibrary);
  const [savingToLibrary, setSavingToLibrary] = useState(false);
  const [showFullSummary, setShowFullSummary] = useState(false);
  const [showFullAbstract, setShowFullAbstract] = useState(false);
  const [showDiscussModal, setShowDiscussModal] = useState(false);
  const [audioState, setAudioState] = useState<'idle' | 'loading' | 'playing' | 'error'>('idle');
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  const handleFeedback = async (feedbackType: string): Promise<void> => {
    if (!showFeedback || !article.pmid) return;
    
    try {
      setSubmittingFeedback(true);
      await libraryAPI.submitFeedback(article.pmid, feedbackType);
      setFeedback(feedbackType);
      toast.success(`Feedback recorded: ${feedbackType === 'useful' ? 'Useful' : 'Not relevant'}`);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error('Failed to submit feedback');
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const handleSaveToLibrary = async (): Promise<void> => {
    if (savingToLibrary || !article.pmid) return;
    
    try {
      setSavingToLibrary(true);
      if (isInLibrary) {
        await libraryAPI.removeArticle(article.pmid);
        setIsInLibrary(false);
        toast.success('Removed from your library');
        if (onLibraryChange) onLibraryChange(article.pmid, false);
      } else {
        await libraryAPI.saveArticle(article.pmid);
        setIsInLibrary(true);
        toast.success('Added to your library');
        if (onLibraryChange) onLibraryChange(article.pmid, true);
      }
    } catch (error) {
      console.error('Error updating library:', error);
      toast.error('Failed to update library');
    } finally {
      setSavingToLibrary(false);
    }
  };

  // Check if AI summary is unavailable
  const isAISummaryUnavailable = article.ai_summary === "Abstract or full text not available for AI summary.";

  // Audio Summary handler — reuses existing single-article audio endpoints
  const handleAudioSummary = async () => {
    if (!article.pmid || audioState === 'loading') return;
    setAudioState('loading');
    try {
      // First check if audio already exists
      const status = await articlesAPI.getAudioSummary(article.pmid);
      if (status.status === 'ready' && status.audio_url) {
        setAudioUrl(status.audio_url);
        setAudioState('playing');
        return;
      }
      // Generate if not already pending
      if (status.status !== 'pending') {
        await articlesAPI.generateAudio(article.pmid);
      }
      // Poll for completion (max 30s)
      for (let i = 0; i < 15; i++) {
        await new Promise(r => setTimeout(r, 2000));
        const poll = await articlesAPI.getAudioSummary(article.pmid);
        if (poll.status === 'ready' && poll.audio_url) {
          setAudioUrl(poll.audio_url);
          setAudioState('playing');
          return;
        }
        if (poll.status === 'failed') throw new Error('Audio generation failed');
      }
      toast.info('Audio is being generated. Check back in a moment.');
      setAudioState('idle');
    } catch {
      setAudioState('error');
      toast.error('Could not generate audio summary.');
    }
  };
  
  // Function to get truncated summary (1-2 lines, ~150 characters)
  const getTruncatedSummary = (text: string): string => {
    if (!text) return '';
    if (text.length <= 150) return text;
    let truncated = text.substring(0, 150);
    const lastSpace = truncated.lastIndexOf(' ');
    if (lastSpace > 100) {
      truncated = truncated.substring(0, lastSpace);
    }
    return truncated + '...';
  };

  // Truncate abstract to ~200 chars at word boundary
  const getTruncatedAbstract = (text: string): string => {
    if (!text) return '';
    if (text.length <= 200) return text;
    let truncated = text.substring(0, 200);
    const lastSpace = truncated.lastIndexOf(' ');
    if (lastSpace > 120) {
      truncated = truncated.substring(0, lastSpace);
    }
    return truncated + '...';
  };

  return (
    <Card className="hover:shadow-lg transition-shadow relative bg-white" data-testid="article-card">
      <CardContent className="p-6">
        {/* Save to Library Ribbon */}
        {context !== 'library' && (
          <div className="absolute top-4 right-4">
            <Button
              variant={isInLibrary ? "default" : "outline"}
              size="sm"
              onClick={handleSaveToLibrary}
              disabled={savingToLibrary}
              data-testid="save-to-library-btn"
              className={`flex items-center gap-1.5 text-xs ${
                isInLibrary 
                  ? 'bg-green-600 hover:bg-green-700 text-white' 
                  : 'border-blue-600 text-blue-600 hover:bg-blue-50'
              }`}
            >
              {isInLibrary ? (
                <>
                  <BookmarkCheck className="h-3.5 w-3.5" />
                  Saved
                </>
              ) : (
                <>
                  <Bookmark className="h-3.5 w-3.5" />
                  Save to Library
                </>
              )}
            </Button>
          </div>
        )}

        {/* Top: Topic Tags and Study Type Badges */}
        <div className="flex flex-wrap gap-2 mb-4 pr-32">
          {article.mesh_terms && article.mesh_terms.slice(0, 3).map((term, idx) => (
            <Badge key={idx} variant="outline" className="text-xs py-0.5 px-2">
              {term}
            </Badge>
          ))}
          {article.design_tags && article.design_tags.map((tag, idx) => {
            const isHighValue = ['meta-analysis', 'randomized controlled trial', 'systematic review', 'guideline', 'review'].some(
              hvd => tag.toLowerCase().includes(hvd)
            );
            return (
              <Badge 
                key={`design-${idx}`} 
                className={`text-xs py-0.5 px-2 ${
                  isHighValue 
                    ? 'bg-green-100 text-green-800 border-green-300' 
                    : 'bg-gray-100 text-gray-700 border-gray-300'
                }`}
              >
                {tag}
              </Badge>
            );
          })}
          {!article.abstract && (
            <Badge variant="outline" className="text-xs py-0.5 px-2 bg-yellow-50 text-yellow-700 border-yellow-300">
              Abstract Only
            </Badge>
          )}
        </div>

        {/* Title and View Publication Link */}
        <div className="mb-4">
          <h3 
            className="responsive-title text-lg sm:text-xl font-bold text-gray-900 leading-tight mb-3 cursor-pointer hover:text-blue-600 transition-colors"
            onClick={() => navigate(`/article/${article.pmid}`)}
            data-testid="article-title"
          >
            {article.title}
          </h3>
          <a
            href={article.url || `https://pubmed.ncbi.nlm.nih.gov/${article.pmid}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 hover:underline font-medium"
            data-testid="view-publication-link"
          >
            <ExternalLink className="h-4 w-4" />
            View Publication
          </a>
          <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
            <span className="font-medium">{article.journal}</span>
            <span>•</span>
            <span>{article.pub_date}</span>
          </div>

          {/* Audio Summary CTA — library context only, behind flag */}
          {digestAudioLinks && context === 'library' && article.pmid && (
            <div className="mt-3" data-testid="audio-summary-cta-wrapper">
              {audioState === 'playing' && audioUrl ? (
                <audio
                  controls
                  autoPlay
                  src={audioUrl}
                  className="w-full max-w-md h-8"
                  data-testid="audio-summary-player"
                  onEnded={() => setAudioState('idle')}
                />
              ) : (
                <Button
                  size="sm"
                  onClick={handleAudioSummary}
                  disabled={audioState === 'loading'}
                  data-testid="audio-summary-cta-btn"
                  className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white shadow-sm"
                >
                  {audioState === 'loading' ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                      Generating...
                    </>
                  ) : audioState === 'error' ? (
                    <>
                      <Headphones className="h-4 w-4 mr-1.5" />
                      Retry Audio Summary
                    </>
                  ) : (
                    <>
                      <Headphones className="h-4 w-4 mr-1.5" />
                      Audio Summary
                    </>
                  )}
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Abstract (Truncated with Collapsible Full View) */}
        {article.abstract && (
          <div className="mb-4" data-testid="article-abstract-section">
            <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
              <div className="text-sm text-gray-700 leading-relaxed">
                {showFullAbstract ? (
                  <p className="whitespace-pre-line">{article.abstract}</p>
                ) : (
                  <p>{getTruncatedAbstract(article.abstract)}</p>
                )}
              </div>
              {article.abstract.length > 200 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowFullAbstract(!showFullAbstract)}
                  className="mt-1.5 text-blue-600 hover:text-blue-800 p-0 h-auto font-medium flex items-center gap-1 text-xs"
                  data-testid="toggle-abstract-btn"
                >
                  {showFullAbstract ? (
                    <>
                      <ChevronUp className="h-3.5 w-3.5" />
                      Hide full abstract
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-3.5 w-3.5" />
                      Show full abstract
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Two-Column Layout: Metadata (Left) and Key Findings (Right) */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-4">
          {/* Left Column: Metadata (1 column width) */}
          <div className="space-y-3 lg:col-span-1">
            {/* Population */}
            <div className="flex items-start gap-3">
              <Users className="h-5 w-5 text-gray-500 mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">Population</div>
                <div className="text-sm text-gray-600 leading-relaxed">
                  {article.population || "Not specified"}
                </div>
              </div>
            </div>

            {/* Study Size */}
            <div className="flex items-start gap-3">
              <BarChart3 className="h-5 w-5 text-gray-500 mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">Study Size</div>
                <div className="text-sm text-gray-600 leading-relaxed">
                  {article.study_size || "Not reported"}
                </div>
              </div>
            </div>

            {/* Key Questions */}
            <div className="flex items-start gap-3">
              <HelpCircle className="h-5 w-5 text-gray-500 mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">Key Questions</div>
                <div className="text-sm text-gray-600 leading-relaxed">
                  {article.key_questions || "Not specified"}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Key Findings (3 columns width) */}
          <div className="lg:col-span-3">
            {article.key_findings && article.key_findings.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Lightbulb className="h-5 w-5 text-blue-700" />
                  <div className="text-xs font-bold text-blue-900 uppercase tracking-wider">Key Findings</div>
                </div>
                {typeof article.key_findings === 'string' ? (
                  <p className="text-sm text-blue-900 leading-relaxed">{article.key_findings}</p>
                ) : (
                  <ul className="text-sm text-blue-900 space-y-2">
                    {article.key_findings.map((finding, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="text-blue-600 font-bold mt-0.5">•</span>
                        <span className="flex-1 leading-relaxed">{finding}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Feedback Buttons */}
        {showFeedback && !isAISummaryUnavailable && (
          <div className="flex items-center gap-3 mt-4 pt-4 border-t flex-wrap">
            <span className="text-xs text-gray-600">Was this helpful?</span>
            <Button
              variant={feedback === 'useful' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleFeedback('useful')}
              disabled={submittingFeedback}
              data-testid="feedback-useful-btn"
              className={feedback === 'useful' ? 'bg-green-600 hover:bg-green-700' : ''}
            >
              <ThumbsUp className="h-3.5 w-3.5 mr-1.5" />
              Useful
            </Button>
            <Button
              variant={feedback === 'not_relevant' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleFeedback('not_relevant')}
              disabled={submittingFeedback}
              data-testid="feedback-not-relevant-btn"
              className={feedback === 'not_relevant' ? 'bg-red-600 hover:bg-red-700' : ''}
            >
              <ThumbsDown className="h-3.5 w-3.5 mr-1.5" />
              Not Relevant
            </Button>
            {/* Phase 6: Discuss button — only when ENABLE_COMMUNITY_V2=true */}
            {communityV2 && article.pmid && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDiscussModal(true)}
                data-testid="discuss-article-btn"
                className="ml-auto text-blue-600 border-blue-200 hover:bg-blue-50"
              >
                <MessageSquare className="h-3.5 w-3.5 mr-1.5" />
                Discuss
              </Button>
            )}
          </div>
        )}

        {/* Phase 6: Discuss modal */}
        {showDiscussModal && article.pmid && (
          <DiscussModal
            pmid={article.pmid}
            articleTitle={article.title || ''}
            specialtyId={specialtyId}
            onClose={() => setShowDiscussModal(false)}
          />
        )}

        {/* AI Summary (Bottom - Expandable) */}
        {article.ai_summary && !isAISummaryUnavailable && (
          <div className="mt-4 pt-4 border-t">
            <div className="flex items-center gap-2 mb-2">
              <svg className="h-4 w-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <div className="text-xs font-bold text-gray-700 uppercase tracking-wide">AI Summary</div>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
              <div className="text-sm text-gray-800 leading-relaxed">
                {article.ai_summary === "Full manuscript or abstract are not available to generate AI summary" ? (
                  <p className="italic text-gray-600">{article.ai_summary}</p>
                ) : showFullSummary ? (
                  <p className="whitespace-pre-line">{article.ai_summary}</p>
                ) : (
                  <p>{getTruncatedSummary(article.ai_summary)}</p>
                )}
              </div>
              {article.ai_summary !== "Full manuscript or abstract are not available to generate AI summary" && 
               article.ai_summary.length > 150 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowFullSummary(!showFullSummary)}
                  className="mt-2 text-blue-600 hover:text-blue-800 p-0 h-auto font-medium flex items-center gap-1"
                >
                  {showFullSummary ? (
                    <>
                      <ChevronUp className="h-4 w-4" />
                      Hide AI summary
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-4 w-4" />
                      Show full AI summary
                    </>
                  )}
                </Button>
              )}
              {article.ai_summary !== "Full manuscript or abstract are not available to generate AI summary" && (
                <p className="text-xs text-yellow-700 mt-3 font-medium">
                  Warning: AI-generated summary for educational purposes. Always review full article and primary sources before clinical decisions.
                </p>
              )}
            </div>
          </div>
        )}

        {/* AI Summary for unavailable abstracts */}
        {isAISummaryUnavailable && (
          <div className="mt-4 pt-4 border-t">
            <div className="bg-gray-50 border border-gray-300 rounded-md p-4">
              <p className="text-sm text-gray-600 italic">Abstract or full text article was not available for an AI summary.</p>
            </div>
          </div>
        )}

        {article.pmid && (
          <CrossAppLinks
            pmid={article.pmid}
            originApp={context === 'digest' ? 'screen' : context === 'library' ? 'learn' : 'screen'}
            title={article.title}
            className="-mx-6 mt-4 px-6 rounded-none border-x-0"
          />
        )}
      </CardContent>
    </Card>
  );
};

export default ArticleCard;
